/**
 * Page Jurisprudence CJUE
 * Route : /jurisprudence/cjue
 *
 * Deux modes :
 *  - Browse  : scroll paginé des arrêts CJUE indexés dans cjue_arrets
 *  - Recherche : recherche sémantique dans la collection cjue_arrets
 *
 * Spécificités futures prévues :
 *  - Filtres par matière (protection des données, concurrence, libertés fondamentales…)
 *  - Filtres par formation (Grande Chambre, chambre à 5, à 3)
 *  - Vue "décisions françaises qui citent cet arrêt"
 *  - Lien vers EUR-Lex / CELEX
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { Loader } from '@/components/Loader';
import {
  PublicHeader, PublicFooter, MCPBadge,
} from '@/components/jurisprudence';
import { SmartSearchBar } from '@/components/jurisprudence/SmartSearch';
import { SearchModeToggle, useSearchMode, type SearchMode } from '@/components/jurisprudence/SearchModeToggle';


const SITE_URL     = 'https://smartlawyer.ai';
const OG_IMAGE     = `${SITE_URL}/api/fiches/og-image/default.png`;
const LIMIT        = 20;
const DISPLAY_STEP = 10;

// Filtres sur le champ `directory_codes` (codes EUR-Lex réels stockés dans Qdrant)
const SECTORS = [
  'PIIC',  // Propriété intellectuelle, industrielle et commerciale
  'CONC',  // Concurrence
  'FISC',  // Fiscalité / TVA
  'LCPE',  // Libre circulation des personnes et établissement
  'AGRI',  // Agriculture et pêche
  'ELSJ',  // Espace de liberté, sécurité et justice
  'ENVI',  // Environnement
  'PSOC',  // Politique sociale
  'CONS',  // Protection des consommateurs
  'TRAN',  // Transports
];

// Labels lisibles pour les codes directory_codes (clés i18n cjue.sectors.*)
const SECTOR_LABELS: Record<string, string> = Object.fromEntries(
  SECTORS.map(code => [code, `cjue.sectors.${code}`]),
);

// ── Types CJUE (calqués sur le schema réel de cjue_arrets) ───────────────────
interface CJUEArret {
  id:                      string;
  celex:                   string;
  ecli?:                   string;
  date_publication?:       string;
  year_of_resource?:       string;
  sector?:                 string;
  directory_codes?:        string[];
  subject_matter?:         string[];
  judicial_procedure_type?: string;
  origin_country?:         string | string[];
  language_procedure?:     string;
  resource_legal_type?:    string;
  resource_type?:          string;
  source?:                 string;
  text_language?:          string;
  probleme?:               string;
  solution_text?:          string;
  faits?:                  string;
  cited_by_count?:         number;
  eurlex_url?:             string;
  based_on_treaty?:        string[];
  citing?:                 string[];
  keywords?:               string[];
}

// Convertit un CELEX en référence lisible (partagé avec PublicCJUEFiche)
// `court` et `suffix` sont des clés i18n (cjue.court.* / cjue.celex_suffix.*)
function celexToRef(celex: string): { ref: string; court: string; suffix: string } {
  const m = celex.match(/^6(\d{4})(CC|CJ|C|T|F)(O?)(\d{3,5})/i);
  if (!m) return { ref: celex, court: 'cjue.court.CJUE', suffix: '' };
  const [, year, courtRaw, isOrd, numStr] = m;
  const num       = parseInt(numStr, 10);
  const rawUp     = courtRaw.toUpperCase();
  const courtCode = rawUp === 'CJ' ? 'C' : rawUp === 'CC' ? 'C' : rawUp;
  const courtName: Record<string, string> = {
    C: 'cjue.court.C', CJ: 'cjue.court.C', T: 'cjue.court.T', F: 'cjue.court.F', CC: 'cjue.court.CC',
  };
  const suffix = rawUp === 'CC' ? 'cjue.celex_suffix.concl' : isOrd ? 'cjue.celex_suffix.ord' : '';
  return {
    ref:   `${courtCode}-${num}/${year.slice(2)}`,
    court: courtName[rawUp] ?? 'cjue.court.CJUE',
    suffix,
  };
}

// Mapping nom complet → drapeau emoji (valeurs réelles du champ origin_country)
const COUNTRY_FLAG: Record<string, string> = {
  // EU 27
  'Germany': '🇩🇪', 'Italy': '🇮🇹', 'France': '🇫🇷', 'Netherlands': '🇳🇱',
  'Belgium': '🇧🇪', 'Spain': '🇪🇸', 'Austria': '🇦🇹',
  'Greece': '🇬🇷', 'Poland': '🇵🇱', 'Portugal': '🇵🇹', 'Ireland': '🇮🇪',
  'Luxembourg': '🇱🇺', 'Denmark': '🇩🇰', 'Romania': '🇷🇴', 'Hungary': '🇭🇺',
  'Czechia': '🇨🇿', 'Czech Republic': '🇨🇿',
  'Bulgaria': '🇧🇬', 'Sweden': '🇸🇪', 'Finland': '🇫🇮',
  'Slovenia': '🇸🇮', 'Latvia': '🇱🇻', 'Lithuania': '🇱🇹', 'Slovakia': '🇸🇰',
  'Estonia': '🇪🇪', 'Cyprus': '🇨🇾', 'Malta': '🇲🇹', 'Croatia': '🇭🇷',
  // Variantes EUR-Lex
  'The Netherlands': '🇳🇱', 'Holland': '🇳🇱',
  'United Kingdom': '🇬🇧', 'United Kingdom of Great Britain and Northern Ireland': '🇬🇧',
  'Great Britain': '🇬🇧',
  // Hors UE — fréquents en renvoi préjudiciel
  'United States': '🇺🇸', 'Switzerland': '🇨🇭', 'Norway': '🇳🇴',
  'Iceland': '🇮🇸', 'Liechtenstein': '🇱🇮', 'Turkey': '🇹🇷', 'Türkiye': '🇹🇷',
  'Serbia': '🇷🇸', 'Ukraine': '🇺🇦', 'Russia': '🇷🇺',
  // Territoires & pays tiers présents dans cjue_arrets (rollup 2026-06)
  'Japan': '🇯🇵', 'Canada': '🇨🇦', 'South Korea': '🇰🇷',
  'Hong Kong': '🇭🇰', 'Philippines': '🇵🇭', 'Taiwan': '🇹🇼',
  'Thailand': '🇹🇭', 'China': '🇨🇳', 'Singapore': '🇸🇬',
  'Australia': '🇦🇺', 'Brazil': '🇧🇷', 'Argentina': '🇦🇷',
  'Saudi Arabia': '🇸🇦', 'Madagascar': '🇲🇬', 'Burkina Faso': '🇧🇫',
  'Democratic Republic of the Congo': '🇨🇩',
  'Netherlands Antilles': '🇦🇳', 'Aruba': '🇦🇼',
  'Gibraltar': '🇬🇮', 'Bermuda': '🇧🇲',
  // "Provisional data" = métadonnée EUR-Lex, pas un pays → pas de drapeau
};
const LANGUAGE_FLAG: Record<string, string> = {
  'German': '🇩🇪', 'French': '🇫🇷', 'Italian': '🇮🇹', 'Spanish': '🇪🇸',
  'Dutch': '🇳🇱', 'Polish': '🇵🇱', 'Portuguese': '🇵🇹', 'Greek': '🇬🇷',
  'Czech': '🇨🇿', 'Romanian': '🇷🇴', 'Hungarian': '🇭🇺', 'Bulgarian': '🇧🇬',
  'Swedish': '🇸🇪', 'Finnish': '🇫🇮', 'Danish': '🇩🇰', 'Slovak': '🇸🇰',
  'Slovenian': '🇸🇮', 'Lithuanian': '🇱🇹', 'Latvian': '🇱🇻', 'Estonian': '🇪🇪',
  'Croatian': '🇭🇷', 'Maltese': '🇲🇹', 'Irish': '🇮🇪', 'English': '🇬🇧',
};

function resolveFlags(origin_country?: string | string[], language_procedure?: string): string[] {
  const countries = Array.isArray(origin_country)
    ? origin_country
    : (origin_country ? [origin_country] : []);
  const fromCountry = countries.map(c => COUNTRY_FLAG[c]).filter(Boolean) as string[];
  if (fromCountry.length > 0) return fromCountry;
  const fromLang = language_procedure ? LANGUAGE_FLAG[language_procedure] : undefined;
  return fromLang ? [fromLang] : [];
}

// ── Card CJUE ────────────────────────────────────────────────────────────────
function CJUEArretCard({ arret }: { arret: CJUEArret }) {
  const { t } = useTranslation();
  const citedBy     = arret.cited_by_count ?? 0;
  const displayDate = arret.date_publication?.slice(0, 10) || arret.year_of_resource || '';
  const snippet     = arret.probleme || arret.solution_text || arret.faits || '';
  const { ref: celexRefBase, court: courtKey, suffix: suffixKey } = celexToRef(arret.celex);
  const celexRef  = suffixKey ? `${celexRefBase} ${t(suffixKey)}` : celexRefBase;
  const courtName = t(courtKey);
  const isGrandeChambre = (arret.judicial_procedure_type ?? '').toLowerCase().includes('grande') ||
                          (arret.judicial_procedure_type ?? '').toLowerCase().includes('grand');

  const flags = resolveFlags(arret.origin_country, arret.language_procedure);

  const mainCode    = (arret.directory_codes ?? []).find(c => !c.includes('.') && SECTOR_LABELS[c]);
  const sectorLabel = mainCode ? t(SECTOR_LABELS[mainCode]) : null;

  return (
    <Link
      to={`/jurisprudence/cjue/${encodeURIComponent(arret.celex)}`}
      className="group block rounded-xl border border-blue-100 dark:border-blue-900 border-l-4 border-l-blue-500 bg-card hover:border-blue-300 hover:shadow-md transition-all duration-200 p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          {/* Badges */}
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              🇪🇺 {courtName}
            </span>
            {arret.judicial_procedure_type && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                isGrandeChambre
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 border-amber-200'
                  : 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border-violet-200'
              }`}>
                {isGrandeChambre ? '⭐ ' : ''}{arret.judicial_procedure_type}
              </span>
            )}
            {sectorLabel && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                {sectorLabel}
              </span>
            )}
            {flags.slice(0, 3).map((f, i) => (
              <span key={i} className="text-[14px] leading-none">{f}</span>
            ))}
          </div>

          {/* Référence lisible */}
          <p className="text-base font-bold text-foreground group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors"
             style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
            {celexRef}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
            {displayDate && <span>{displayDate}</span>}
            <span className="text-muted-foreground/40 font-mono text-[10px]">{arret.celex}</span>
          </p>
        </div>

        {/* Badge citations FR */}
        {citedBy > 0 && (
          <span className={`flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
            citedBy >= 20
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
              : 'bg-muted text-muted-foreground'
          }`}>
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            {t('cjue.card.fr_decisions', { count: citedBy })}
          </span>
        )}
      </div>

      {/* Extrait */}
      {snippet && (
        <p className="text-[13px] italic text-muted-foreground leading-relaxed line-clamp-2 mt-2"
           style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
          {snippet}
        </p>
      )}

      {/* Relations & fondements */}
      {((arret.based_on_treaty?.length ?? 0) > 0 || (arret.citing?.length ?? 0) > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-blue-50 dark:border-blue-900/30">
          {/* Traités / articles cités */}
          {arret.based_on_treaty?.slice(0, 3).map((tr, i) => (
            <span key={i}
                  className="inline-flex items-center text-[10px] font-mono px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
              {tr}
            </span>
          ))}
          {(arret.based_on_treaty?.length ?? 0) > 3 && (
            <span className="text-[10px] text-muted-foreground">+{arret.based_on_treaty!.length - 3}</span>
          )}
          {/* Arrêts CJUE cités */}
          {(arret.citing?.length ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground ml-auto">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
              {t('cjue.card.cites', { count: arret.citing!.length })}
            </span>
          )}
        </div>
      )}
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PublicCJUE() {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode]               = useState<'browse' | 'search'>('browse');
  const [query, setQuery]             = useState(searchParams.get('q') || '');
  const [sector, setSector]           = useState(searchParams.get('sector') || '');
  const [loading, setLoading]         = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // Browse state
  const [browseResults, setBrowseResults] = useState<CJUEArret[]>([]);
  const [browseTotal, setBrowseTotal]     = useState(0);
  const [page, setPage]                   = useState(0);
  const cursorsRef = useRef<(string | null)[]>([null]);

  // Search state
  const [allResults, setAllResults]     = useState<CJUEArret[]>([]);
  const [results, setResults]           = useState<CJUEArret[]>([]);
  const [displayCount, setDisplayCount] = useState(0);
  const [searchTime, setSearchTime]     = useState<number | null>(null);

  // Mode de recherche
  const [searchMode, setSearchMode] = useState<SearchMode>('libre');
  const toggleMode = useSearchMode(searchMode, setSearchMode, 'libre');

  // Stats CJUE (depuis le cache compute_stats)
  const [cjueStats, setCjueStats] = useState<{
    total: number; arrets_principe: number; rgpd_count: number;
    cjue_count: number;
    cjue_with_fr_impact: number;
    cjue_grande_chambre: number;
    cjue_with_summary: number;
    cjue_total_fr_citations: number;
    cjue_year_range: { min?: number; max?: number };
    cjue_by_directory: { code: string; count: number }[];
    cjue_top_cited: { celex: string; cited_by_count: number; year: string }[];
  } | null>(null);

  useEffect(() => {
    fetch('/api/fiches/corpus-stats')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCjueStats(d))
      .catch(() => null);
  }, []);

  // ── Browse ──────────────────────────────────────────────────────────────────
  const fetchBrowse = useCallback(async (pageNum: number, sec: string) => {
    setLoading(true);
    setError(null);
    const cursor = pageNum === 0 ? null : (cursorsRef.current[pageNum] ?? null);
    const params = new URLSearchParams({ limit: String(LIMIT) });
    if (cursor) params.set('cursor', cursor);
    if (sec)    params.set('sector', sec);

    try {
      const res  = await fetch(`/api/cjue/browse?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBrowseResults(data.results || []);
      setBrowseTotal(data.total || 0);
      if (data.next_cursor != null) cursorsRef.current[pageNum + 1] = data.next_cursor;
    } catch {
      setError(t('cjue.errors.load'));
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // ── Recherche sémantique ────────────────────────────────────────────────────
  const doSearch = useCallback(async (q: string, sec: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setAllResults([]);
    setResults([]);
    setDisplayCount(0);
    const t0 = performance.now();

    try {
      const res = await fetch('/api/cjue/search', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, limit: 50, sector: sec || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data    = await res.json();
      const fetched: CJUEArret[] = data.results || [];
      setAllResults(fetched);
      setResults(fetched.slice(0, DISPLAY_STEP));
      setDisplayCount(DISPLAY_STEP);
      setSearchTime(Math.round(performance.now() - t0));
    } catch {
      setError(t('cjue.errors.search'));
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const q   = searchParams.get('q') || '';
    const sec = searchParams.get('sector') || '';
    setQuery(q);
    setSector(sec);
    if (q) { setMode('search'); doSearch(q, sec); }
    else   { setMode('browse'); fetchBrowse(0, sec); }
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleGuidedSearch = useCallback((q: string, filters: Record<string, string>) => {
    const sec = filters.sector ?? '';
    setQuery(q);
    setSector(sec);
    setMode('search');
    const params: Record<string, string> = { q };
    if (sec) params.sector = sec;
    setSearchParams(params);
    doSearch(q, sec);
  }, [doSearch, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      setMode('browse'); setPage(0); cursorsRef.current = [null];
      setSearchParams(sector ? { sector } : {});
      fetchBrowse(0, sector);
      return;
    }
    setMode('search');
    const params: Record<string, string> = { q: query };
    if (sector) params.sector = sector;
    setSearchParams(params);
    doSearch(query, sector);
  };

  const handleSector = (sec: string) => {
    setSector(sec); setPage(0); cursorsRef.current = [null];
    if (mode === 'search' && query.trim()) doSearch(query, sec);
    else fetchBrowse(0, sec);
    const params: Record<string, string> = {};
    if (query) params.q = query;
    if (sec)   params.sector = sec;
    setSearchParams(params);
  };

  const handleReset = () => {
    setQuery(''); setSector(''); setMode('browse'); setPage(0);
    cursorsRef.current = [null];
    setSearchParams({});
    fetchBrowse(0, '');
  };

  const goToPage = (p: number) => {
    if (p > 0 && cursorsRef.current[p] === undefined) return;
    setPage(p);
    fetchBrowse(p, sector);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadMore = () => {
    const next = displayCount + DISPLAY_STEP;
    setResults(allResults.slice(0, next));
    setDisplayCount(next);
  };

  const totalPages    = Math.ceil(browseTotal / LIMIT);
  const hasNextCursor = cursorsRef.current[page + 1] != null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{t('cjue.meta.title')}</title>
        <meta name="description" content={t('cjue.meta.description')} />
        <link rel="canonical" href={`${SITE_URL}/jurisprudence/cjue`} />
        <meta property="og:title"       content={t('cjue.meta.title')} />
        <meta property="og:description" content={t('cjue.meta.og_description')} />
        <meta property="og:image"       content={OG_IMAGE} />
        <meta property="og:url"         content={`${SITE_URL}/jurisprudence/cjue`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">

          {/* Breadcrumb */}
          <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-foreground transition-colors">{t('cjue.breadcrumb.home')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <Link to="/browse" className="hover:text-foreground transition-colors">{t('nav.jurisprudence')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-foreground">{t('cjue.abbr')}</span>
          </nav>

          {/* ── En-tête épuré ── */}
          <div className="mb-6">
            {/* Titre + cross-links */}
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇪🇺</span>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold leading-tight">{t('cjue.header.corpus')} <em className="not-italic text-primary">{t('cjue.abbr')}</em></h1>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                <Link to="/jurisprudence/principes" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  ⭐ {t('cjue.header.corpus_principles')}
                  {cjueStats && <span className="font-mono text-[10px] opacity-60">{cjueStats.arrets_principe.toLocaleString(locale)}</span>}
                </Link>
                <span className="text-border">·</span>
                <Link to="/jurisprudence/rgpd" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  🔒 {t('cjue.header.corpus_gdpr')}
                  {cjueStats && <span className="font-mono text-[10px] opacity-60">{cjueStats.rgpd_count.toLocaleString(locale)}</span>}
                </Link>
                <span className="text-border">·</span>
                <Link to="/reglements" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  📋 {t('cjue.header.eu_regulations')}
                </Link>
              </div>
            </div>

            {/* Stats en ligne */}
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50">
                <span className="text-base font-bold text-foreground tabular-nums">
                  {cjueStats ? cjueStats.cjue_count.toLocaleString(locale) : <span className="inline-block w-10 h-4 bg-indigo-100 dark:bg-indigo-800/40 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('cjue.stats.judgments')}</span>
              </div>
              {cjueStats && cjueStats.cjue_with_fr_impact > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/40">
                  <span className="text-base font-bold text-amber-700 dark:text-amber-400 tabular-nums">{cjueStats.cjue_with_fr_impact.toLocaleString(locale)}</span>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('cjue.stats.influential_fr')}</span>
                </div>
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-800/40">
                <span className="text-base font-bold text-violet-700 dark:text-violet-400 tabular-nums">
                  {cjueStats ? cjueStats.cjue_grande_chambre.toLocaleString(locale) : <span className="inline-block w-10 h-4 bg-violet-100 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('cjue.stats.grand_chamber')}</span>
              </div>
              {cjueStats?.cjue_year_range?.min && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                  <span className="text-base font-bold text-foreground tabular-nums">{cjueStats.cjue_year_range.min}–{cjueStats.cjue_year_range.max}</span>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('cjue.stats.coverage')}</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Zone de recherche unifiée ── */}
          <div className="rounded-2xl border border-border bg-muted/40 px-5 sm:px-6 pt-4 pb-4 mb-6">
            {/* Toprow : compteur + toggle */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-[13px] font-bold text-foreground">
                <span className="text-lg leading-none">🇪🇺</span>{' '}
                {cjueStats
                  ? <><strong className="text-primary">{cjueStats.cjue_count.toLocaleString(locale)}</strong> {t('cjue.search.indexed_judgments')}</>
                  : t('cjue.search.corpus_title')}
              </p>
              <SearchModeToggle mode={searchMode} onChange={toggleMode} />
            </div>
            <div className="h-px bg-border/60 mb-3" />

            {searchMode === 'guidee' ? (
              <SmartSearchBar context="cjue" onSmartSearch={handleGuidedSearch} searching={loading} />
            ) : (
              <>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="flex-1 flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary/30 transition-all">
                    <svg className="h-4 w-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder={t('cjue.search.placeholder')}
                      className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/60"
                    />
                    {query && (
                      <button type="button" onClick={handleReset} aria-label={t('cjue.search.clear')} className="text-muted-foreground hover:text-foreground transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <button type="submit" disabled={loading}
                    className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0">
                    {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('cjue.search.submit')}
                  </button>
                </form>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <button onClick={() => handleSector('')}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${!sector ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background border-border text-muted-foreground hover:border-indigo-400'}`}>
                    {t('cjue.search.all_sectors')}
                  </button>
                  {SECTORS.map(s => (
                    <button key={s} onClick={() => handleSector(s)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${sector === s ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background border-border text-muted-foreground hover:border-indigo-400'}`}>
                      {SECTOR_LABELS[s] ? t(SECTOR_LABELS[s]) : s}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Compteur */}
          {initialized && !loading && (
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {mode === 'browse' ? (
                  <>
                    <strong className="text-foreground">{browseTotal.toLocaleString(locale)}</strong> {t('cjue.results.judgments', { count: browseTotal })}
                    {sector && <> · <span className="text-foreground">{SECTOR_LABELS[sector] ? t(SECTOR_LABELS[sector]) : t('cjue.results.sector', { code: sector })}</span></>}
                    {page > 0 && <span className="ml-2 text-muted-foreground/60">· {t('cjue.results.page_lower', { page: page + 1 })}</span>}
                  </>
                ) : (
                  <>
                    <strong className="text-foreground">{allResults.length}</strong> {t('cjue.results.results_for', { count: allResults.length })}{' '}
                    <em className="text-foreground">{t('cjue.results.quoted_query', { query })}</em>
                    {searchTime != null && (
                      <span className="ml-2 text-xs text-muted-foreground/60">
                        ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                      </span>
                    )}
                  </>
                )}
              </p>
              {mode === 'search' && (
                <button onClick={handleReset}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {t('cjue.results.see_all')}
                </button>
              )}
            </div>
          )}

          {/* Contenu */}
          {!initialized || (loading && !browseResults.length && !results.length) ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader className="!size-8" />
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-24">
              <p className="text-5xl mb-4">⚠️</p>
              <p className="font-semibold mb-2">{error}</p>
              <button onClick={() => mode === 'browse' ? fetchBrowse(page, sector) : doSearch(query, sector)}
                className="mt-4 text-sm text-primary underline">{t('common.retry')}</button>
            </div>
          ) : mode === 'browse' ? (
            <>
              {browseResults.length === 0 ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-5xl mb-4">🇪🇺</p>
                  <p className="font-semibold text-foreground mb-1">
                    {sector ? t('cjue.empty.none_in_sector', { sector: SECTOR_LABELS[sector] ? t(SECTOR_LABELS[sector]) : sector }) : t('cjue.empty.none_indexed')}
                  </p>
                  <p className="text-sm">{t('cjue.empty.import_in_progress')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {browseResults.map(a => <CJUEArretCard key={a.id} arret={a} />)}
                </div>
              )}

              {(totalPages > 1 || hasNextCursor || page > 0) && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button disabled={page === 0 || loading} onClick={() => goToPage(page - 1)}
                    className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                    ← {t('common.previous')}
                  </button>
                  <span className="text-sm text-muted-foreground px-3">{t('cjue.results.page', { page: page + 1 })}</span>
                  <button disabled={!hasNextCursor || loading} onClick={() => goToPage(page + 1)}
                    className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                    {t('common.next')} →
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {results.length === 0 ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-5xl mb-4">🔎</p>
                  <p className="font-semibold text-foreground mb-1">{t('cjue.empty.none_found')}</p>
                  <p className="text-sm">{t('cjue.empty.try_reference')}</p>
                  <button onClick={handleReset} className="mt-4 text-sm text-primary underline block mx-auto">
                    {t('cjue.results.see_all')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {results.map(a => <CJUEArretCard key={a.id} arret={a} />)}
                  {displayCount < allResults.length && (
                    <div className="flex justify-center pt-4">
                      <button onClick={loadMore}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl border-2 border-border hover:border-blue-400 hover:bg-accent text-sm font-medium transition-all">
                        {t('cjue.results.load_more', { count: allResults.length - displayCount })}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </main>

        <PublicFooter />
      </div>

      <MCPBadge />
    </>
  );
}
